import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Alert
} from '@mui/material';
import EventCard from '../components/EventCard';
import HeroSection from '../components/HeroSection';
import { Event, SportType } from '../types/event';
import { eventApi, requestApi } from '../utils/api';
import { eventBus, EVENTS } from '../utils/events';

interface EventsProps {
  searchSport: string;
  refreshTrigger?: number;
  onEventClick?: (eventId: string) => void;
}

const Events: React.FC<EventsProps> = ({ searchSport, refreshTrigger, onEventClick }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [levelFilter, setLevelFilter] = useState<string>('');

  const [localRefresh, setLocalRefresh] = useState(0);

  useEffect(() => {
    const handleEventUpdate = () => {
      setLocalRefresh(prev => prev + 1);
    };

    const unsubscribe = eventBus.on(EVENTS.EVENT_UPDATED, handleEventUpdate);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        // Use search sport as activity filter (sport name like "Tennis", "Badminton")
        const activityFilter = searchSport || undefined;

        // Fetch events and user requests in parallel
        const [eventsData, userRequests] = await Promise.all([
          eventApi.getEvents(activityFilter, undefined, levelFilter || undefined),
          requestApi.getUserRequests().catch(() => []) // Fail gracefully if requests fail
        ]);

        // Filter out past events and canceled events
        const now = new Date();
        const upcomingEvents = eventsData.filter(event =>
          new Date(event.time) > now && event.status !== 'CANCELED'
        );

        // Adjust occupied spots based on user's accepted requests
        // This is a client-side fix because the backend might not be updating occupied_spots immediately
        const adjustedEvents = upcomingEvents.map(event => {
          const myRequest = userRequests.find(r => r.event_id === event.id && r.status === 'ACCEPTED');

          // If I have an accepted request, ensure occupied_spots includes me
          // We assume if occupied_spots is 0 but I am accepted, it should be at least 1
          // This is a heuristic: if backend says 0 but I am joined, make it 1.
          // Ideally backend should be correct.
          let occupied = event.occupied_spots;
          if (myRequest && occupied === 0) {
            occupied = 1;
          }

          return {
            ...event,
            occupied_spots: occupied
          };
        });

        setEvents(adjustedEvents);
        setError(null);
      } catch (err) {
        setError('Failed to load events. Please try again later.');
        console.error('Error fetching events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [searchSport, refreshTrigger, localRefresh, levelFilter]); // Re-fetch when search sport changes or refresh is triggered

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff'
      }}
    >
      <HeroSection />
      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 6 }, px: { xs: 2, sm: 3, md: 5, lg: 10 } }}>
        {/* Events Section Title */}
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 'bold',
            color: '#000',
            mb: { xs: 3, md: 4 },
            fontSize: { xs: '18px', sm: '20px', md: '22px' }
          }}
        >
          Events near you
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress sx={{ color: '#533B7D' }} />
          </Box>
        )}

        {error && !loading && (
          <Alert
            severity="error"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiAlert-icon': { color: '#ff6b6b' }
            }}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Events Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)'
                },
                gap: { xs: '16px', sm: '24px', md: '32px' },
                maxWidth: '1280px',
                margin: '0 auto'
              }}
            >
              {events.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onClick={() => onEventClick && onEventClick(event.id)}
                  onLikeToggle={async (eventId, isCurrentlyLiked) => {
                    try {
                      if (isCurrentlyLiked) {
                        const result = await eventApi.unlikeEvent(eventId);
                        setEvents(prev => prev.map(e =>
                          e.id === eventId
                            ? { ...e, is_liked_by_user: false, like_count: result.like_count }
                            : e
                        ));
                      } else {
                        const result = await eventApi.likeEvent(eventId);
                        setEvents(prev => prev.map(e =>
                          e.id === eventId
                            ? { ...e, is_liked_by_user: true, like_count: result.like_count }
                            : e
                        ));
                      }
                    } catch (err) {
                      console.error('Failed to toggle like:', err);
                    }
                  }}
                />
              ))}
            </Box>

            {events.length === 0 && (
              <Box sx={{ textAlign: 'center', mt: 8 }}>
                <Typography variant="h6" sx={{ color: '#666' }}>
                  No events found
                </Typography>
                <Typography variant="body1" sx={{ color: '#999', mt: 1 }}>
                  Try adjusting your search criteria
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default Events;
